/**
 * Helpers de détection de doublons Pinterest pour le journal des
 * publications (S7-PR2). Vit dans un fichier séparé du composant React
 * pour respecter la règle `react-refresh/only-export-components` —
 * un fichier de composant n'exporte que des composants.
 */

import type { PublicationJournalEntry } from '@/hooks/useWorkPublicationsJournal'

export interface DuplicateGroup {
  groupKey: string
  boardId: string
  draftLabel: string
  publishedBucket: string
  items: PublicationJournalEntry[] // sorted ASC by published_at
}

/** Fenêtre de bucketization temporelle — 2 Pins published dans le même
 * intervalle de 60s sur le même (board, draft) sont considérés doublons.
 * Paramètre figé par le plan S7-PR2. */
const TIME_BUCKET_MS = 60_000

/**
 * Groupe les Pins Pinterest `published` par triplet
 * `(board_id, draft_version_label, published_at_bucket_60s)`.
 * Garde uniquement les groupes ≥ 2 items. Sort chaque groupe par
 * `published_at` ASC (le plus ancien d'abord).
 */
export function groupPinterestDuplicates(entries: PublicationJournalEntry[]): DuplicateGroup[] {
  const pinterestActive = entries.filter(
    (e) => e.platform === 'pinterest' && e.state === 'published',
  )

  const buckets = new Map<string, PublicationJournalEntry[]>()
  for (const entry of pinterestActive) {
    const boardId = readBoardId(entry) ?? 'unknown'
    const draftLabel = entry.draft_version_label ?? 'sans draft'
    const ts = entry.published_at
      ? Math.floor(new Date(entry.published_at).getTime() / TIME_BUCKET_MS)
      : 0
    const key = `${boardId}|${draftLabel}|${ts}`
    const list = buckets.get(key)
    if (list) {
      list.push(entry)
    } else {
      buckets.set(key, [entry])
    }
  }

  const groups: DuplicateGroup[] = []
  for (const [key, items] of buckets.entries()) {
    if (items.length < 2) continue
    const sorted = [...items].sort((a, b) =>
      (a.published_at ?? '').localeCompare(b.published_at ?? ''),
    )
    const [boardId, draftLabel] = key.split('|')
    const bucketDate = sorted[0]?.published_at
      ? new Date(sorted[0].published_at!).toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—'
    groups.push({
      groupKey: key,
      boardId,
      draftLabel,
      publishedBucket: bucketDate,
      items: sorted,
    })
  }
  return groups
}

/** Sélection initiale : tous les items sauf le 1er (le plus ancien) de
 * chaque groupe — pré-cochés pour suppression. */
export function buildInitialDuplicateSelection(groups: DuplicateGroup[]): Set<number> {
  const selection = new Set<number>()
  for (const group of groups) {
    for (let i = 1; i < group.items.length; i++) {
      selection.add(group.items[i]!.id)
    }
  }
  return selection
}

/** Compte total de Pins à supprimer si on prend la pré-sélection (= total
 * d'items - 1 par groupe, le 1er étant conservé). */
export function countPinterestDuplicates(groups: DuplicateGroup[]): number {
  return groups.reduce((acc, g) => acc + (g.items.length - 1), 0)
}

/** `state_metadata` est typé `dict | None` côté API ; on extrait `board_id`
 * defensively. Le BE pose systématiquement `state_metadata.board_id` lors
 * de la persistance d'un Pin (`PinterestAdapter._persist_published_pin`). */
function readBoardId(entry: PublicationJournalEntry): string | null {
  const meta = entry.state_metadata
  if (meta && typeof meta === 'object' && 'board_id' in meta) {
    const value = (meta as { board_id?: unknown }).board_id
    if (typeof value === 'string' && value) return value
  }
  return null
}
